import { useEffect, useMemo, useState } from "react"
import { RoutePath, useRoutesHooks } from "../../../../../features/routes"
import { Async, Badge, DetailFieldProps, DetailPanel, FieldType, FieldValue, Tab, UIPermission, useGetCrumbs } from "../../../../../features/ui"
import { TableName } from "../../../../../tables"
import { define } from "../../../../../utils/typeUtils"
import { CompanyUserName, useSystemStateHooks } from "../../../../system"
import { Product, ProductPrices } from "../../../state/types"
import { Data, Labels, useDataTypesHooks, useDescribeHooks } from "../../../../../features/data-types"
import { useSupplierLoaders } from "../../../../purchases"
import { useUnitTypeLoaders } from "../../../../stock"
import { ProductPricesDetail } from "./productPricesDetail"
import { ProductMeasuresDetail } from "./productMeasuresDetail"
import { useProductPricesLoaders } from "../../../loaders/productPricesLoaders"

export const ProductDetail = () => {
  const { dataType } = useRoutesHooks().parseUrl()
  const product = dataType! as Product

  const [productPrices, setProductPrices] = useState<ProductPrices>()

  const { userHasPermission } = useSystemStateHooks()
  const readOnly = !userHasPermission(UIPermission.SAVE_PRODUCTS) || product.archived
  const { describe } = useDescribeHooks()
  const { formatDateTime } = useDataTypesHooks()

  const productPricesLoaders = useProductPricesLoaders()
  const supplierLoaders = useSupplierLoaders()
  const unitTypeLoaders = useUnitTypeLoaders()

  useEffect(() => {
    productPricesLoaders().loadProductPrices(product.id).then(setProductPrices)
  }, [])
  
  const title = describe(TableName.PRODUCTS, product)
  const crumbs = useGetCrumbs(
    'Productos',
    RoutePath.PRODUCTS,
    product.name
  )

  const productData: Data = useMemo(() => {
    const { measures, unitMeasures, equivalences, ...productProps } = product
    return productProps
  }, [product])

  const fields: DetailFieldProps[] = [
    {
      name: 'code',
      type: FieldType.TEXT,
      label: 'Código',
      render: (value?: FieldValue) => <>#{value}</>
    },
    {
      name: 'name',
      type: FieldType.TEXT,
      label: 'Nombre'
    },
    {
      name: 'supplierId',
      type: FieldType.TABLE,
      table: TableName.SUPPLIERS,
      label: 'Proveedor',
      render: () => <Async
        loadFn={async() => {
          const supplier = await supplierLoaders().loadSupplier(product.supplierId)
          return <Badge label={define(supplier).name} />
        }}
      />
    },
    {
      name: 'supplierCode',
      type: FieldType.TEXT,
      label: 'Código Interno'
    },
    {
      name: 'categoryId',
      type: FieldType.TABLE,
      table: TableName.CATEGORIES,
      label: 'Categoría'
    },
    {
      name: 'type',
      type: FieldType.TEXT,
      label: 'Tipo de Producto',
      render: () => <>{Labels.productTypeLabel(product.type)}</>
    },
    {
      name: 'loted',
      type: FieldType.BOOLEAN,
      label: 'Contiene Partidas'
    },
    {
      name: 'measure',
      type: FieldType.NUMBER,
      label: 'Medida Principal',
      render: () => {
        const unitType = define(unitTypeLoaders().findUnitType(product.unitTypeId))
        return <>{product.units} {product.units ? unitType.abbreviation : unitType.name}</>
      }
    }
  ]
  if (product.description) {
    const descriptionField = {
      name: 'description',
      type: FieldType.TEXT,
      label: 'Descripción'
    }
    fields.splice(5, 0, descriptionField)
  }
  if (product.secondaryUnitTypeId) {
    const secondaryMeasureField = {
      name: 'secondaryMeasure',
      type: FieldType.NUMBER,
      label: 'Medida Secundaria',
      render: () => {
        const unitType = define(unitTypeLoaders().findUnitType(product.secondaryUnitTypeId))
        return <>{define(product.secondaryUnits)} {unitType.abbreviation}</>
      }
    }
    fields.push(secondaryMeasureField)
  }

  if (product.negativeStateUserId) {
    fields.push({
      name: 'negativeStateUserId',
      type: FieldType.TEXT,
      label: 'Archivado por',
      render: () => {
        const dateStr = product.negativeStateAt ? ` - ${formatDateTime(new Date(product.negativeStateAt))}` : ''
        return <><CompanyUserName userId={product.negativeStateUserId!} />{dateStr}</>
      }
    })
  }

  const tabs: Tab[] = [
    {
      id: 'prices',
      title: 'Precios',
      content: <ProductPricesDetail product={product} productPrices={productPrices} />
    }
  ]
  if (product.measures.length > 0) {
    const additionalMeasuresTab = {
      id: 'additionalMeasures',
      title: 'Medidas Adicionales',
      content: <ProductMeasuresDetail product={product} />
    }
    tabs.splice(0, 0, additionalMeasuresTab)
  }

  return (
    <DetailPanel
      tableName={TableName.PRODUCTS}
      title={title}
      crumbs={crumbs}
      data={productData}
      fields={fields}
      icon='box-seam'
      tabs={tabs}
      readOnly={readOnly}
    />
  )
}
